import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Type,
  TemplateRef,
  Output,
  EventEmitter,
  ViewChild,
  ContentChild,
  AfterViewInit,
  Injector,
  ChangeDetectorRef,
  Renderer2,
  ViewContainerRef,
  Inject,
  Optional,
} from "@angular/core";
import { NzDrawerComponent } from "ng-zorro-antd/drawer";
import { NzConfigService, WithConfig } from "ng-zorro-antd/core/config";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { NgStyleInterface, NzSafeAny } from "ng-zorro-antd/core/types";
import { DrawerRef } from "@webapp/ui/drawer/abstracts/drawer-ref";
import { DrawerPlacement } from "@webapp/ui/drawer/drawer.models";
import { DrawerContentDirective } from "@webapp/ui/drawer/directives/drawer-content.directive";
import { CdkPortalOutlet, ComponentPortal } from "@angular/cdk/portal";
import { Overlay, OverlayKeyboardDispatcher } from "@angular/cdk/overlay";
import { FocusTrapFactory } from "@angular/cdk/a11y";
import { Directionality } from "@angular/cdk/bidi";
import { DOCUMENT } from "@webapp/core/factories/document.factory";

@Component({
  selector: "ui-drawer",
  exportAs: "uiDrawer",
  templateUrl: "./drawer.component.html",
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiDrawerComponent<T = NzSafeAny, R = NzSafeAny, D = NzSafeAny> extends NzDrawerComponent implements AfterViewInit {
  @Input("drawerContent") nzContent!: TemplateRef<{ $implicit: D; drawerRef: DrawerRef<R> }> | Type<T>;
  @Input("drawerCloseIcon") nzCloseIcon: string | TemplateRef<void> = "close";
  @Input("drawerClosable") @InputBoolean() nzClosable = true;
  @Input("drawerMaskClosable") @WithConfig() @InputBoolean() nzMaskClosable = true;
  @Input("drawerMask") @WithConfig() @InputBoolean() nzMask = true;
  @Input("drawerCloseOnNavigation") @WithConfig() @InputBoolean() nzCloseOnNavigation = true;
  @Input("drawerNoAnimation") @InputBoolean() nzNoAnimation = false;
  @Input("drawerKeyboard") @InputBoolean() nzKeyboard = true;
  @Input("drawerTitle") nzTitle?: string | TemplateRef<unknown>;
  @Input("drawerFooter") nzFooter?: string | TemplateRef<unknown>;
  @Input("drawerPlacement") nzPlacement: DrawerPlacement = "right";
  @Input("drawerMaskStyle") nzMaskStyle: NgStyleInterface = {};
  @Input("drawerBodyStyle") nzBodyStyle: NgStyleInterface = {};
  @Input("drawerWrapClassName") nzWrapClassName?: string;
  @Input("drawerWidth") nzWidth: number | string = 256;
  @Input("drawerHeight") nzHeight: number | string = 256;
  @Input("drawerZIndex") nzZIndex = 1000;
  @Input("drawerOffsetX") nzOffsetX = 0;
  @Input("drawerOffsetY") nzOffsetY = 0;

  @Input("drawerVisible") isOpen = false;

  get drawerVisible(): boolean {
    return this.isOpen;
  }

  @Output() readonly drawerOnViewInit = new EventEmitter<void>();
  @Output() readonly drawerOnClose = new EventEmitter<MouseEvent>();
  @Output() readonly drawerVisibleChange = new EventEmitter<boolean>();

  @ViewChild("drawerTemplate", { static: true }) drawerTemplate!: TemplateRef<void>;
  @ViewChild(CdkPortalOutlet, { static: false }) bodyPortalOutlet?: CdkPortalOutlet;
  @ContentChild(DrawerContentDirective, { static: true, read: TemplateRef })
  contentFromContentChild?: TemplateRef<NzSafeAny>;

  private drawerComponentInstance: T | null = null;

  constructor(
    drawerCdr: ChangeDetectorRef,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Optional() @Inject(DOCUMENT) drawerDocument: NzSafeAny,
    drawerConfigService: NzConfigService,
    drawerRenderer: Renderer2,
    drawerOverlay: Overlay,
    private drawerInjector: Injector,
    drawerChangeDetectorRef: ChangeDetectorRef,
    drawerFocusTrapFactory: FocusTrapFactory,
    drawerViewContainerRef: ViewContainerRef,
    drawerOverlayKeyboardDispatcher: OverlayKeyboardDispatcher,
    @Optional() drawerDirectionality: Directionality
  ) {
    super(
      drawerCdr,
      drawerDocument,
      drawerConfigService,
      drawerRenderer,
      drawerOverlay,
      drawerInjector,
      drawerChangeDetectorRef,
      drawerFocusTrapFactory,
      drawerViewContainerRef,
      drawerOverlayKeyboardDispatcher,
      drawerDirectionality
    );
  }

  ngAfterViewInit(): void {
    // do not invoke the parent ngAfterViewInit
    this.drawerAttachBodyContent();
    if (this.nzOnViewInit.observers.length) {
      setTimeout(() => {
        this.nzOnViewInit.emit();
      });
    }
  }

  private drawerAttachBodyContent(): void {
    this.bodyPortalOutlet!.dispose();

    if (this.nzContent instanceof Type) {
      const childInjector = Injector.create({
        parent: this.drawerInjector,
        providers: [{ provide: DrawerRef, useValue: this }],
      });
      const componentPortal = new ComponentPortal<T>(this.nzContent, null, childInjector);
      const componentRef = this.bodyPortalOutlet!.attachComponentPortal(componentPortal);
      this.drawerComponentInstance = componentRef.instance;
      Object.assign(componentRef.instance, this.nzContentParams);
      componentRef.changeDetectorRef.detectChanges();
    }
  }

  close(result?: R): void {
    this.drawerComponentInstance = null;
    super.close(result);
  }

  getContentComponent(): T | null {
    return this.drawerComponentInstance;
  }
}
